import React, { memo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import {
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  SkipForward,
  Ban,
  AlertTriangle,
  RotateCcw,
  ArrowRight,
  DollarSign
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@vehix/portal/components/ui/tooltip';
import { Badge } from '@vehix/portal/components/ui/badge';
import { getStatusBorderColor } from './PipelineStatusBadge';
import type { PipelineStep, StepStatus } from '../../services/pipelineOrchestrator';

// ============================================================================
// STEP NODE DATA (passed via React Flow node.data)
// ============================================================================

export interface StepNodeData {
  step: PipelineStep;
  onStepClick?: (step: PipelineStep) => void;
}

// ============================================================================
// STATUS ICONS
// ============================================================================

const STATUS_ICONS: Record<StepStatus, React.ElementType> = {
  pending: Clock,
  running: Loader2,
  completed: CheckCircle,
  failed: XCircle,
  skipped: SkipForward,
  cancelled: Ban,
  partial_success: AlertTriangle
};

const STATUS_ICON_COLORS: Record<StepStatus, string> = {
  pending: 'text-muted-foreground',
  running: 'text-blue-500',
  completed: 'text-green-500',
  failed: 'text-red-500',
  skipped: 'text-muted-foreground/60',
  cancelled: 'text-orange-500',
  partial_success: 'text-yellow-500'
};

// ============================================================================
// HELPERS
// ============================================================================

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '-';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `${hrs}h ${remainMins}m`;
}

function formatCount(count: number | null): string {
  if (count === null || count === undefined) return '-';
  return count.toLocaleString();
}

function formatCost(cost: number): string {
  if (!cost) return '';
  return `$${cost.toFixed(2)}`;
}

// ============================================================================
// NODE COMPONENT
// ============================================================================

// eslint-disable-next-line max-lines-per-function, complexity
const PipelineStepNodeInner: React.FC<NodeProps<StepNodeData>> = ({ data }) => {
  const { step, onStepClick } = data;
  const Icon = STATUS_ICONS[step.status] || Clock;
  const iconColor = STATUS_ICON_COLORS[step.status] || 'text-muted-foreground';
  const borderColor = getStatusBorderColor(step.status);
  const isRunning = step.status === 'running';
  const isSkipped = step.status === 'skipped';
  const progressPct = (step.config?.['progress_pct'] as number) || undefined;

  return (
    <TooltipProvider>
      <div
        role='button'
        tabIndex={0}
        className={`
          relative rounded-lg border-2 bg-card p-3 shadow-sm
          transition-all hover:shadow-md hover:border-primary/50 cursor-pointer
          min-w-[180px] max-w-[220px]
          ${borderColor}
          ${isRunning ? 'animate-pulse' : ''}
          ${isSkipped ? 'opacity-50' : ''}
        `}
        onClick={() => onStepClick?.(step)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') onStepClick?.(step);
        }}
      >
        {/* Handles for React Flow edges */}
        <Handle type='target' position={Position.Left} className='!bg-border !w-2 !h-2' />
        <Handle type='source' position={Position.Right} className='!bg-border !w-2 !h-2' />

        {/* Header: Icon + Step Name */}
        <div className='flex items-start gap-2 mb-2'>
          <Icon
            className={`h-4 w-4 mt-0.5 shrink-0 ${iconColor} ${isRunning ? 'animate-spin' : ''}`}
          />
          <span className='text-xs font-medium leading-tight line-clamp-2'>{step.step_name}</span>
        </div>

        {/* Progress bar for running steps */}
        {isRunning && progressPct !== undefined && (
          <div className='w-full h-1.5 bg-muted rounded-full mb-2 overflow-hidden'>
            <div
              className='h-full bg-blue-500 rounded-full transition-all duration-500'
              style={{ width: `${Math.min(progressPct, 100)}%` }}
            />
          </div>
        )}

        {/* I/O Metrics */}
        {(step.input_count !== null || step.output_count !== null) && (
          <div className='flex items-center gap-1 text-[10px] text-muted-foreground mb-1.5'>
            <span>{formatCount(step.input_count)}</span>
            <ArrowRight className='h-2.5 w-2.5' />
            <span>{formatCount(step.output_count)}</span>
            {step.skipped_count !== null && step.skipped_count > 0 && (
              <span className='ml-1 text-yellow-500'>({formatCount(step.skipped_count)} skip)</span>
            )}
          </div>
        )}

        {/* Footer: Duration + Cost */}
        <div className='flex items-center justify-between text-[10px] text-muted-foreground'>
          <span>{formatDuration(step.duration_seconds)}</span>
          {step.cost_usd > 0 && (
            <span className='flex items-center gap-0.5'>
              <DollarSign className='h-2.5 w-2.5' />
              {formatCost(step.cost_usd)}
            </span>
          )}
        </div>

        {/* Retry indicator */}
        {step.attempt > 1 && (
          <Badge
            variant='outline'
            className='absolute -top-2 -right-2 text-[9px] px-1 py-0 h-4 bg-card'
          >
            <RotateCcw className='h-2.5 w-2.5 mr-0.5' />
            {step.attempt}/{step.max_attempts}
          </Badge>
        )}

        {/* Error tooltip for failed / partial_success nodes */}
        {(step.status === 'failed' || step.status === 'partial_success') && step.error_message && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='absolute -bottom-2 -right-2'>
                <AlertTriangle
                  className={`h-4 w-4 bg-card rounded-full ${
                    step.status === 'partial_success' ? 'text-yellow-500' : 'text-red-500'
                  }`}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side='bottom' className='max-w-xs'>
              <p
                className={`text-xs font-medium mb-1 ${
                  step.status === 'partial_success' ? 'text-yellow-500' : 'text-red-500'
                }`}
              >
                {step.error_category ||
                  (step.status === 'partial_success' ? 'Partial Success' : 'Error')}
              </p>
              <p className='text-xs text-muted-foreground'>{step.error_message}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

export const PipelineStepNode = memo(PipelineStepNodeInner);
